import { makeAutoObservable, runInAction, trace } from 'mobx';
import { dominosStore } from '../../../main';
import { showInfoPopup, subscribeToClose } from '../../../modules/InfoPopup';
import { getProperTextForm } from '../../utils/string/string';
import { goFish } from '../api';
import { getTextFromAppDictionary } from '../dictionary';
import { redirectToCart, redirectToMenu } from '../navigation';
import { DominosRootStore } from './index';

export class PromotionStore {
    dominosStore: DominosRootStore;

    /**
     * Modified promotion data with steps
     *
     * @type {DominosPromotion}
     * @memberof PromotionStore
     */
    promotion: DominosPromotion;

    /**
     * Active promotion step
     *
     * @memberof PromotionStore
     */
    currentStep = 0;

    /**
     * Data to rendered on promo view
     *
     * @type {AppPromotionProduct[][]}
     * @memberof PromotionStore
     */
    promotionStepsProducts: AppPromotionProduct[][];

    /**
     * Flag for active promotion pizza in configurator
     *
     * @type {string}
     * @memberof PromotionStore
     */
    promotionPizzaId = '';

    /**
     * ID of started, active promotion
     *
     * @type {string}
     * @memberof PromotionStore
     */
    unfinishedPromotion: string;

    /**
     * Loading
     *
     * @memberof PromotionStore
     */
    working = false;

    /**
     * Promotion in edit mode
     *
     * @memberof PromotionStore
     */
    edit = false;

    /**
     * Product that has already been edited.  
     * Used to determine if the promotional banner should be displayed.
     *
     * @type {boolean}
     * @memberof PromotionStore
     */
    alreadyEditedProduct: boolean = false;

    // Analytics object from cart
    analyticsPromotion: AnalyticsStepObj[] = []

    /**
     * Creates an instance of PromotionStore.
     *
     * @param {DominosRootStore} store
     * @memberof PromotionStore
     */
    constructor(store: DominosRootStore) {
        makeAutoObservable(this);
        this.dominosStore = store;
    }

    /**
     * Flag that some product is already edited.
     *
     * @param {boolean} boolean - True if some product is edited.
     * @memberof PromotionStore
     */
    setAlreadyEditedProduct(boolean: boolean): void {
        this.alreadyEditedProduct = boolean;
    }

    /**
     * Setup for promotion
     *
     * @param {PromotionProductsList} promotionProductsFomApi
     * @param {string} localid
     * @memberof PromotionStore
     */
    startPromotion = (
        promotionProductsFomApi: PromotionProductsList,
        promoId: string,
        localId: string,
        categoryId: string,
        promoFromState?: CartCouponResponse,
        step?: string,
        goToReconfigurePizza?: () => void,
        prodCardIdInMultiProductStep?:string
    ) => {
        const editMode = promoFromState ? true : false;

        // Parse all product and convert them into AppPromotionProducts
        if (editMode) {
            this.edit = true;

            // Edited product updates
            this.promotionStepsProducts = promotionProductsFomApi.Group.map(group => {

                const editGroup = promoFromState.CartGroups.find(g => g.GroupId === group.Guid);

                return group.Product.map(product => {
                    const isMultiProductStep = group.RequiredQuantity !== 1
                    const groupArr: CartProduct[] =
                        editGroup[product.Type === 1 ? 'CartPizzas' : 'CartProducts'];

                    const groupProduct = groupArr.find(p => p.Id === product.Id);
                    
                    const quantity = groupProduct?.CartId === prodCardIdInMultiProductStep && isMultiProductStep ? 0 : (groupProduct && groupProduct.Count) || 0

                    return {
                        ...product,
                        Quantity: quantity
                    };
                });
            });
        } else {
            this.edit = false;
            // Fresh start so all has to be 0
            this.promotionStepsProducts = promotionProductsFomApi.Group.map(group => {
                return group.Product.map(product => {
                    return {
                        ...product,
                        Quantity: 0
                    };
                });
            });
        }

        // Setup promotion body
        this.promotion = {
            name: promotionProductsFomApi.Name,
            localId: localId,
            id: promoId,
            cartId: editMode ? promoFromState.CartId : '',
            steps: promotionProductsFomApi.Group.map(
                (group, index): PromotionStep => {
                    const grProd: AppPromotionProduct[] = [];
                    let name: string = this.getStepName(group.RequiredQuantity);

                    const isMultiProductStep = group.RequiredQuantity !== 1

                    if (editMode) {
                        const editGroup = promoFromState.CartGroups[index];

                        // Simple products
                        editGroup.CartProducts.map(cartP => {
                            if (
                                !(
                                    isMultiProductStep &&
                                    prodCardIdInMultiProductStep === cartP.CartId
                                )
                            ) {
                                const appProduct = this.promotionStepsProducts[index].find(
                                    listP => listP.Id === cartP.Id
                                );

                                if (appProduct) {
                                    grProd.push({ ...appProduct });
                                }
                            }
                        });
                        // Pizzas
                        editGroup.CartPizzas.map(cartP => {
                            if (!(isMultiProductStep && prodCardIdInMultiProductStep === cartP.CartId)) {
                                const appProduct = this.promotionStepsProducts[index].find(
                                    listP => listP.Id === cartP.Id
                                );

                                if (appProduct) {
                                    const pizza: CartPizzaProduct = {
                                        Id: cartP.Id,
                                        Count: cartP.Count,
                                        Size: cartP.Size,
                                        Flavor: cartP.Flavor,
                                        FlavorPulseCode: cartP.PulseCode,
                                        Options: cartP.Options
                                    };
                                    grProd.push(
                                        Object.assign({}, appProduct, {
                                            Pizza: { ...pizza }
                                        })
                                    );
                                }
                            }
                        });

                        const prodsName = grProd.map(p => p.Name).join(', ');
                        name =
                            group.RequiredQuantity !== 1
                                ? getTextFromAppDictionary('promotion.steps.title.complete') +
                                ': ' +
                                prodsName
                                : prodsName;
                    }

                    return {
                        index: index,
                        guid: group.Guid,
                        name: name,
                        complete: editMode ? true : false,
                        visible: editMode ? true : index > 0 ? false : true,
                        required: group.RequiredQuantity,
                        max: group.MaximumQuantityAllowed,
                        ismultistep: isMultiProductStep,
                        products: grProd
                    };
                }
            )
        };
        // Mark started promotion
        this.unfinishedPromotion = `${categoryId}|${promoId}`;

        //
        if (editMode) {
            // Set proper step
            if (step && parseInt(step) > 1) {
                this.changeCurrentStep(parseInt(step) - 1);
            }
            goToReconfigurePizza && goToReconfigurePizza()
        }
    };

    /**
     * Ends promotion current step.
     * It will not validate the step just complete it.
     * For validation use isStepReady for current or checkStepReady.
     * When this was last unfinished step promotion will be validated:
     * - if all steps are ready promotion is added to cart
     * - if not user is redirected to first unfinished step
     *
     * @memberof PromotionStore
     */
    completePromotionStep = () => {
        if (this.isPromoRdy) {
            // Update simple step name (multistep should be updated anyway)
            if (!this.isMultiStep) {
                // Take the name from first (and only) product
                this.promotion.steps[this.currentStep].name = this.promotion.steps[
                    this.currentStep
                ].products[0].Name;
            }

            // Complete this current step
            this.promotion.steps[this.currentStep].complete = true;

            // Check if there is more unfinished steps
            const nextUnfinishedStepIndex = this.promotion.steps.findIndex(
                step => !this.checkStepReady(step.index)
            );

            // Move view to the top
            this.dominosStore.moveToTop();

            // All seems right
            if (nextUnfinishedStepIndex == -1) {
                // Finish promotion if it's not edit mode
                if (!this.edit) {
                    this.working = true;
                    this.dominosStore.cartStore
                        .addPromotionToCart(this.promotion.name, this.createCartCouponProduct(), this.analyticsPromotion)
                        .then(errorMessage => {
                            this.working = false;
                            if (!errorMessage) {
                                console.log('%cPROMO: promocja dodana do koszyka', 'color:violet');
                                this.finishPromotion();
                            }
                        });
                }
                // Case when the only step in promotion is multistep so completion can close promo
                else if (this.promotion.steps.length == 1 && this.isMultiStep) {
                    this.editPromotion();
                }
            }
            // Something is not ready yet
            else {
                const nextStep = this.currentStep + 1;
                // Reveal the next step
                if (
                    nextStep < this.promotion.steps.length &&
                    !this.promotion.steps[nextStep].visible
                ) {
                    this.currentStep = nextStep;
                    this.promotion.steps[this.currentStep].visible = true;
                }
                // Change step to first unfinished if end was reached
                else {
                    this.currentStep = nextUnfinishedStepIndex;
                }
            }
        }
    };

    /**
     * Confirm promotion changes and return to cart.
     * If there are some unfinished steps message will appear and action will be stopped.
     *
     * @memberof PromotionStore
     */
    editPromotion = () => {
        this.working = true;

        // Check if there are unfinished steps
        const nextUnfinishedStepIndex = this.promotion.steps.findIndex(
            step => !this.checkStepReady(step.index)
        );

        if (nextUnfinishedStepIndex > -1) {
            this.changeCurrentStep(nextUnfinishedStepIndex);
            showInfoPopup({ text: getTextFromAppDictionary('promotion.steps.messages.min') });
            this.working = false;
        }
        // All finished so we can confirm changes and return to cart
        else {
            const cartCoupon: CartCouponProductEdit = {
                CartId: this.promotion.cartId,
                ...this.createCartCouponProduct()
            };
            this.dominosStore.cartStore.editPromotionInCart(cartCoupon).then(editResp => {
                this.working = false;
                if (!editResp.Message) {
                    console.log('%cPROMO: promocja zmieniona', 'color:violet');
                    this.finishPromotion();
                    redirectToCart();
                }
            });
        }
    };

    /**
     * Helper for cart object
     *
     * @memberof PromotionStore
     */
    createCartCouponProduct = (): CartCouponProduct => {
        // Promotion object
        const coupon: CartCouponProduct = {
            Id: this.promotion.id,
            Count: 1,
            CartCoupons: []
        };
        // Prepare products from all steps
        this.promotion.steps.map(step => {
            // Group data
            const group: CartCouponGroup = {
                Id: step.guid,
                CartPizzas: [],
                CartProducts: []
            };
            // Parse products in group
            step.products.map(p => {
                const product = {
                    Id: p.Id,
                    Count: p.Quantity
                };
                // Pizza
                if (p.Type === 1) {
                    group.CartPizzas.push(
                        Object.assign(product, {
                            Size: p.Pizza.Size,
                            Flavor: p.Pizza.Flavor,
                            FlavorPulseCode: p.Pizza.FlavorPulseCode,
                            Options: p.Pizza.Options
                        })
                    );
                }
                // Simple
                else if (p.Type === 98) {
                    group.CartProducts.push(product);
                }
            });
            // Add group
            coupon.CartCoupons.push(group);
        });

        return coupon;
    };

    /**
     * Goes forward if current step is simple step and it's ready.
     * If not updates the name
     *
     * @memberof PromotionStore
     */
    manageAfterAdded = () => {
        // When multistep update the name only
        if (this.isMultiStep) {
            // When multistep add product names after base name
            const prodsName = this.promotion.steps[this.currentStep].products
                .map(p => (p.Quantity > 1 ? `${p.Quantity}x ${p.Name}` : p.Name))
                .join(', ');
            // Complete step has different base name
            if (this.edit) {
                if (this.isMultistepReady) {
                    this.dominosStore.moveToTop();                    
                }    
                const prodsName = this.promotion.steps[this.currentStep].products
                    .filter(p => p.newInMultiProductStepChangeTime)
                    .map(p => `${p.Quantity > 1 ? '(' + p.Quantity + 'x' + ')' : ''} ${p.Name}`)
                    .join(', ');
                this.promotion.steps[this.currentStep].name =
                    getTextFromAppDictionary('promotion.steps.title.complete') + ': ' + prodsName;
                return;
            }

            if (this.isMultistepReady) {
                this.promotion.steps[this.currentStep].name =
                    getTextFromAppDictionary('promotion.steps.title.complete') + ': ' + prodsName;
            }
            // Unfinished has only base name when empty (no products)
            else {
                const name = this.getStepName(this.promotion.steps[this.currentStep].required);
                this.promotion.steps[this.currentStep].name = prodsName
                    ? name + ': ' + prodsName
                    : name;
            }
        }
        // If it's single step
        else {
            // We can go to the next one if it's ready
            if (this.isSinglestepReady) {
                this.completePromotionStep();
            }
            // Update the name for unfinished
            else {
                this.promotion.steps[this.currentStep].name = this.getStepName(
                    this.promotion.steps[this.currentStep].required
                );
            }
        }
    };

    /**
     * Returns proper name for unfinished step.
     * required === 1 : simple step
     */
    getStepName = (required: number): string => {
        return required === 1
            ? getTextFromAppDictionary('promotion.steps.title.simple')
            : required === 0
                ? getTextFromAppDictionary('promotion.steps.title.multistep.nolimits')
                : getProperTextForm(
                    getTextFromAppDictionary('promotion.steps.title.multistep'),
                    required
                );
    };

    /**
     * Add/remove (depends on quantity) simple product into store.
     * If ok returns empty string, if not error message
     *
     * @memberof PromotionStore
     */
    addProduct = (quantity: number, productId: string, name: string) => {
        if (this.isPromoRdy) {
            const currStep = this.promotion.steps[this.currentStep];
            // Quantity validation
            const max = currStep.max;
            const newQuantity = this.stepQuantity + quantity;

            // scroll to top when max is reached
            if (max -1 > 0 && newQuantity > max -1 ) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            // Max checks
            if (max > 0 && newQuantity > max) {
                const maxMessage = getProperTextForm(
                    getTextFromAppDictionary('promotion.steps.messages.max'),
                    max
                );
                showInfoPopup({ text: maxMessage });
                return Promise.resolve(maxMessage);
            }

            // Find proper AppPromotionProduct
            const appProduct = this.promotionStepsProducts[this.currentStep].find(
                p => p.Id === productId
            );
            // Update component view
            appProduct.Quantity += quantity;
            if (quantity > 0 && this.isMultiStep) {
                appProduct.newInMultiProductStepChangeTime = new Date().toISOString();
            }

            // Check if product is in store already
            const addedStepProduct = currStep.products.find(p => p.Id === productId);
            // Save in store
            if (addedStepProduct) {
                let indexToRemove = null;
                this.analyticsPromotion.map((step, index) => {
                    if (step.cartProduct && step.cartProduct.id === productId) {
                        step.cartProduct.count += quantity;
                        if (step.cartProduct.count <= 0) {
                            indexToRemove = index;
                        }
                    }
                    return step;
                })
                if (indexToRemove !== null) {
                    this.analyticsPromotion.splice(indexToRemove, 1);
                }

                addedStepProduct.Quantity += quantity;
                if ( this.isMultiStep) {
                    addedStepProduct.newInMultiProductStepChangeTime = addedStepProduct.Quantity > 0 ? new Date().toISOString() : null;
                }
                // Remove if quantity has fallen to 0 or below (just in case)
                if (addedStepProduct.Quantity <= 0) {
                    currStep.products.splice(
                        currStep.products.findIndex(p => p.Id === addedStepProduct.Id),
                        1
                    );
                    currStep.complete = false;
                }
            } else {
                const analyticsStepObj: AnalyticsStepObj = {
                    cartPizza: null,
                    cartProduct: {
                        name: name,
                        id: productId,
                        count: quantity
                    }
                }
                this.analyticsPromotion.push(analyticsStepObj);
                const stepProduct: AppPromotionProduct = { ...appProduct };
                currStep.products.push(stepProduct);
            }

            this.manageAfterAdded();
        }
        return Promise.resolve('');
    };

    addPizza = (name: string, pizza: CartPizzaProduct, analyticsOptions: CartProductResponse[], analyticsSizeName: string, analyticsFlavorName: string) => {
        // Quantity validation
        const max = this.promotion.steps[this.currentStep].max;
        // For pizza it should always be 1 so omit pizza.Count
        const pizzaCount = 1;
        const newQuantity = this.stepQuantity + pizzaCount;
        const analyticsStepObj: AnalyticsStepObj = {
            cartPizza: {
                id: pizza.Id,
                name: name,
                sizeName: analyticsSizeName,
                flavorName: analyticsFlavorName,
                options: analyticsOptions,
                count: pizza.Count
            },
            cartProduct: null
        }
            
        if (max > 0 && newQuantity > max && (!this.edit || (this.edit && !this.promotion.steps[this.currentStep].ismultistep))) {
            const maxMessage = getProperTextForm(
                getTextFromAppDictionary('promotion.steps.messages.max'),
                max
            );
            dominosStore.setDebugMessage(maxMessage);
            // If max was reached all products will be wiped
            this.promotion.steps[this.currentStep].products = [];
            this.analyticsPromotion.pop();

        }
        this.analyticsPromotion.push(analyticsStepObj);

        // Prepare AppPromotionProduct
        const appProduct = this.promotionStepsProducts[this.currentStep].find(
            p => p.Id === this.promotionPizzaId
        );
        // Update quantity (it want be visible but it's needed for counting steps quantity)
        appProduct.Quantity = pizzaCount;

        // Save in store
        const stepProduct: AppPromotionProduct = Object.assign({}, appProduct, {
            Pizza: { ...pizza }
        });
        if(this.edit && this.isMultiStep) {
            stepProduct.newInMultiProductStepChangeTime = new Date().toISOString();
        }
        this.promotion.steps[this.currentStep].products.push(stepProduct);

        this.manageAfterAdded();

        return Promise.resolve('');
    };

    savePromotionPizzaId = (id: string) => {
        this.promotionPizzaId = id;
    };

    releasePromotionPizzaId = () => {
        this.promotionPizzaId = '';
    };

    removePromotion = () => {
        console.log('%cPROMO: usuwam promocję na życzenie', 'color:violet');
        this.finishPromotion();
    };

    finishPromotion = () => {
        this.unfinishedPromotion = '';
        this.currentStep = 0;
        this.promotion = undefined;
        this.promotionStepsProducts = undefined;
        this.analyticsPromotion = [];

        // It’s needed if we go to the promotion from the basket and then need to return to the basket.
        const isBackToCard = localStorage.getItem('isBackToCard');
        if (isBackToCard && isBackToCard === 'true') {
            localStorage.setItem('isBackToCard', 'false');
            redirectToCart();
        } 
    };

    leavingPromotion = () => {
        // Promotion was started but not touched (or products removed)
        if (this.isPromoRdy && this.stepQuantity === 0 && !this.promotionPizzaId) {
            console.log(
                '%cPROMO: kasuję aktualną promocję, bo nie została rozpoczęta',
                'color:violet'
            );
            this.finishPromotion();
        }
    };

    /**
     * Move to specific promotion step
     *
     * @memberof PromotionStore
     */
    changeCurrentStep = (step: number) => {
        if (this.isPromoRdy && step < this.promotion.steps.length) {
            this.currentStep = step;
            // Move view to the top
            this.dominosStore.moveToTop();
        }
    };

    promotionErrorHandler = (message?: string) => {
        showInfoPopup({ text: message || getTextFromAppDictionary('promotion.error') });
        subscribeToClose(() => {
            redirectToMenu();
        });
    };

    /**
     *
     * @param productguid
     * @param currentcategory
     * @param step first step is 1 not zero (it's from url)
     */
    getPromotionDetails = async (
        productguid: string,
        currentcategory: NavigationItem,
        step: string,
        cartid: string,
        goToReconfigurePizza: () => void | null,
        prodCardIdInMultiProductStep?: string
    ) => {
        const localguid = this.dominosStore.localStore.localId;
        return goFish({
            api: 'getpromotiondetails',
            data: { localguid, productguid }
        }).then(
            (response: PromotionProductsList) => {
                // Back-end data validations
                if (response.Status && response.Group) {
                    const allSteps = response.Group;
                    // Check if promo is valid
                    if (allSteps.length === 0) {
                        // No steps
                        this.promotionErrorHandler();
                        response.Status = false;
                        return response;
                    }

                    // Detect empty groups
                    const promotionDataDisrupted = response.Group.find(
                        promo => promo.Product.length === 0
                    );
                    if (promotionDataDisrupted) {
                        this.promotionErrorHandler();
                        response.Status = false;
                        return response;
                    }

                    // Check edit mode
                    if (cartid && !response.IsComplementary) {
                        // Get cart product data
                        return dominosStore.cartStore
                            .getCartProduct(cartid)
                            .then(editedProduct => {
                                if (editedProduct && editedProduct.Type === 2) {
                                    // Update data
                                    this.startPromotion(
                                        response,
                                        productguid,
                                        localguid,
                                        currentcategory.guid,
                                        { ...editedProduct.Data },
                                        step,
                                        goToReconfigurePizza,
                                        prodCardIdInMultiProductStep
                                    );
                                    return response;
                                } else {
                                    console.log(
                                        '%cPROMO: Promo with this CartId was removed from cart or its not promo at all',
                                        'color:red'
                                    );
                                    throw {
                                        Message: getTextFromAppDictionary('messages.errors.cart')
                                    };
                                }
                            })
                            .catch(e => {
                                // Something bad happend: cart failed or no product in cart
                                showInfoPopup({
                                    text:
                                        e.Message ||
                                        getTextFromAppDictionary('messages.errors.server')
                                });
                                subscribeToClose(() => {
                                    redirectToMenu();
                                });
                                return { Status: false } as PromotionProductsList;
                            });
                    }
                    // Normal mode so serve response further
                    else {
                        // Check 'Model I' promo (https://wiki.i-g.pl/display/DP/Promocje - WF.02)
                        // here 
                        if (
                            allSteps.length == 1 && // 1 group
                            allSteps[0].Product.length == 1 && // 1 product
                            allSteps[0].RequiredQuantity === 1 && // not multistep
                            allSteps[0].Product[0].Type == 98 // not pizza
                        ) {
                            // Add to cart
                            this.working = true;

                            // Promotion object
                            const coupon: CartCouponProduct = {
                                Id: productguid,
                                Count: 1,
                                CartCoupons: [
                                    {
                                        Id: allSteps[0].Guid,
                                        CartPizzas: [],
                                        CartProducts: [
                                            {
                                                Id: allSteps[0].Product[0].Id,
                                                Count: 1
                                            }
                                        ]
                                    }
                                ]
                            };

                            return this.dominosStore.cartStore
                                .addPromotionToCart(response.Name, coupon, this.analyticsPromotion)
                                .then(errorMessage => {
                                      this.working = false;
                                      const isBackToCard = localStorage.getItem('isBackToCard');

                                      if (!errorMessage) {
                                          console.log(
                                              '%cPROMO: promocja od razu dodana do koszyka',
                                              'color:violet'
                                          );
                                      }
                                      // Redirect on success or failure
                                      return {
                                          Status: true,
                                          Code:
                                              isBackToCard && isBackToCard === 'true'
                                                  ? 'isBackToCard'
                                                  : 'instant.promo.in.cart'
                                      } as PromotionProductsList;
                                  });
                          }
                        // Setup normal promo
                        else {
                            // Update data
                            this.startPromotion(
                                response,
                                productguid,
                                localguid,
                                currentcategory.guid
                            );
                            return response;
                        }
                    }
                }
                // Data malformed
                else {
                    this.promotionErrorHandler();
                    return response;
                }
            },
            (err: StandardResponse) => {
                // Handle http error
                this.promotionErrorHandler(
                    err.Message || getTextFromAppDictionary('messages.errors.server')
                );
                return { Status: false } as PromotionProductsList;
            }
        );
    };

    /**
     * Used by promotion baner.
     * Returns promotion guid.
     *
     * @memberof PromotionStore
     */
    checkPromotionCode = async (code: string): Promise<UseCouponResponse> => {
        // Local not set - popup will open
        if (this.dominosStore.localStore.checkLocalNotReady()) {
            // Do nothing there
            return { Status: true, Message: '', Code: '', Data: '' };
        }
        // Local is set but inactive (e.g. closed)
        else if (this.dominosStore.localStore.localIsInactive) {
            return {
                Status: false,
                Message: this.dominosStore.localStore.localMessage,
                Code: '',
                Data: ''
            };
        }
        // Check code
        else {
            return goFish({
                api: 'couponsuse',
                data: { Code: code }
            });
        }
    };

    /**
     * Check if step has all required products.
     * Can be used for validation.
     *
     * @param stepIndex
     */
    checkStepReady = (stepIndex: number) => {
        const step = this.promotion.steps[stepIndex];
        if (step) {
            if (step.ismultistep) {
                return (
                    (step.required === 0 && step.products.length > 0) ||
                    (step.required > 1 && this.checkStepQuantity(step) === step.required)
                );
            } else {
                return step.products.length > 0;
            }
        }
        return false;
    };

    /**
     * All products count in step.
     * Do not validate if step exists.
     *
     * @readonly
     * @memberof PromotionStore
     */
    checkStepQuantity = (step: PromotionStep) => {
        return step.products.reduce((pV, cV) => pV + cV.Quantity, 0);
    };

    get promoGuid() {
        return this.promotion?.id;
    }

    get groupGuid() {
        return this.promotion.steps[this.currentStep].guid;
    }

    /**
     * All products count from current step
     *
     * @readonly
     * @memberof PromotionStore
     */
    get stepQuantity() {
        return this.checkStepQuantity(this.promotion.steps[this.currentStep]);
    }

    /**
     * Helper for getting proper step to display
     *
     * @readonly
     * @memberof PromotionStore
     */
    get activePromotionStepComputed() {
        // Return first not complete or last if all are completed
        return (
            this.isPromoRdy &&
            (this.promotion.steps.find(step => !step.complete) ||
                this.promotion.steps[this.promotion.steps.length - 1])
        );
    }

    /**
     * Get first step that is not complete
     *
     * @readonly
     * @memberof PromotionStore
     */
    get activePromotionStep() {
        // Always returns 0 even when promotion is not started
        return this.activePromotionStepComputed ? this.activePromotionStepComputed.index : 0;
    }

    /**
     * Is current step complete without user action
     *
     * @readonly
     * @memberof PromotionStore
     */
    get isAutoStep() {
        return (
            this.isPromoRdy &&
            this.promotion.steps[this.currentStep].products.length === 1 &&
            this.promotion.steps[this.currentStep].required === 1
        );
    }

    /**
     * Is current step multistep
     *
     * @readonly
     * @memberof PromotionStore
     */
    get isMultiStep() {
        return this.isPromoRdy && this.promotion.steps[this.currentStep].ismultistep;
    }

    /**
     * Do promo step is ready to complete
     *
     * @readonly
     * @memberof PromotionStore
     */
    get isStepReady() {
        if (this.isPromoRdy) {
            if (this.isMultiStep) {
                return this.isMultistepReady;
            } else {
                return this.isSinglestepReady;
            }
        }
        return false;
    }

    /**
     * Do single step is ready
     *
     * @readonly
     * @memberof PromotionStore
     */
    private get isSinglestepReady() {
        return this.promotion.steps[this.currentStep].products.length > 0;
    }

    /**
     * Do multistep with all requirements is fullfiled
     *
     * @readonly
     * @memberof PromotionStore
     */
    private get isMultistepReady() {
        return (
            (this.promotion.steps[this.currentStep].required === 0 &&
                this.promotion.steps[this.currentStep].products.length > 0) ||
            (this.promotion.steps[this.currentStep].required > 1 &&
                this.stepQuantity === this.promotion.steps[this.currentStep].required)
        );
    }

    /**
     * General check if promo is loaded and data are ready to use
     *
     * @readonly
     * @memberof PromotionStore
     */
    get isPromoRdy() {
        return typeof this.promotion != 'undefined' && this.promotion.steps.length > 0;
    }
}
